import { ApiError, createAction } from 'redux-api-middleware';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { Dispatch, GetState, ThunkAction } from '../types/Store';
import { HTTP_POST, HTTP_PUT } from './ApiAction';
import { Attachment, BonusActivity, loadBonusActivities } from './BonusActivity';
import { loadBonusTimeline } from './BonusTimeline';
import { loadDashboard } from './Dashboard';

// This reducer manages the upload of activity redemption requests.
export const UPLOADING_BONUS_ACTIVITY_REDEMPTION = 'barmer-bonus-shared/activities/bonus/redemption/UPLOAD' as const;
export const SUCCESS_BONUS_ACTIVITY_REDEMPTION = 'barmer-bonus-shared/activities/bonus/redemption/SUCCESS' as const;
export const FAILED_BONUS_ACTIVITY_REDEMPTION = 'barmer-bonus-shared/activities/bonus/redemption/FAILED' as const;
export const FINISHED_BONUS_ACTIVITY_REDEMPTION = 'barmer-bonus-shared/activities/bonus/redemption/DONE' as const;
export const EDIT_BONUS_ACTIVITY_REDEMPTION = 'barmer-bonus-shared/activities/bonus/redemption/EDIT' as const;

type UploadingActivityRedemption = {
  type: typeof UPLOADING_BONUS_ACTIVITY_REDEMPTION;
  meta: { activities: Array<BonusActivity> };
  payload?: ApiError;
  error?: boolean;
};

type UploadActivityRedemptionSucceeded = {
  type: typeof SUCCESS_BONUS_ACTIVITY_REDEMPTION;
};

type UploadActivityRedemptionFailed = {
  type: typeof FAILED_BONUS_ACTIVITY_REDEMPTION;
  payload: ApiError;
  error: boolean;
};

type UploadActivityRedemptionFinished = {
  type: typeof FINISHED_BONUS_ACTIVITY_REDEMPTION;
};

type EditActivityRedemption = {
  type: typeof EDIT_BONUS_ACTIVITY_REDEMPTION;
  payload?: ApiError;
  error?: boolean;
};

export type DocType = 'image/png' | 'image/jpg' | 'application/pdf';

/**
 * @param type MIME type
 * @param data File reference
 */
export type DocumentData = { type: DocType; data: string };

/**
 * The FormDataPartCreator should prepare a DocumentData object to be submitted in a multipart/form-data part
 *
 * e.g.
 * handler = (document: File | DocumentData) => {
 *    uri: document.data,
 *    type: document.type,
 * };
 */
export type FormDataPartCreator = (document: File | DocumentData) => File;
export type Fetcher = typeof fetch;

export type Action =
  | UploadingActivityRedemption
  | UploadActivityRedemptionSucceeded
  | UploadActivityRedemptionFailed
  | UploadActivityRedemptionFinished
  | EditActivityRedemption;

export const UPLOAD_PROGRESS_IDLE = 'idle' as const;
export const UPLOAD_PROGRESS_UPLOADING = 'uploading' as const;
export const UPLOAD_PROGRESS_FINISHED = 'finished' as const;
export const UPLOAD_PROGRESS_FAILED = 'failed' as const;

export type UploadProgress =
  | typeof UPLOAD_PROGRESS_IDLE
  | typeof UPLOAD_PROGRESS_UPLOADING
  | typeof UPLOAD_PROGRESS_FINISHED
  | typeof UPLOAD_PROGRESS_FAILED;

export type State = {
  progress: UploadProgress;
  activities: Array<BonusActivity> | null | undefined;
  error?: ApiError;
};

const initialState = {
  progress: UPLOAD_PROGRESS_IDLE,
  activities: null,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case UPLOADING_BONUS_ACTIVITY_REDEMPTION:
      return {
        ...state,
        progress: action.error === undefined ? UPLOAD_PROGRESS_UPLOADING : UPLOAD_PROGRESS_FAILED,
        activities: action.meta.activities,
        error: action.error ? action.payload : undefined,
      };
    case SUCCESS_BONUS_ACTIVITY_REDEMPTION:
      return {
        ...state,
        progress: UPLOAD_PROGRESS_FINISHED,
        error: undefined,
      };
    case FAILED_BONUS_ACTIVITY_REDEMPTION:
      return {
        ...state,
        progress: UPLOAD_PROGRESS_FAILED,
        error: action.payload,
      };
    case FINISHED_BONUS_ACTIVITY_REDEMPTION:
      return {
        progress: UPLOAD_PROGRESS_IDLE,
        activities: null,
        error: undefined,
      };
    case EDIT_BONUS_ACTIVITY_REDEMPTION:
      return {
        ...state,
        progress: action.error === undefined ? UPLOAD_PROGRESS_UPLOADING : UPLOAD_PROGRESS_FAILED,
        error: action.error ? action.payload : undefined,
      };
    default:
      return state;
  }
}

/**
 * Query the uploading state in general or for specific activity.
 *
 * @param globalState The Store
 * @param activity Optional whether to check for a specific activity
 */
export function isUploading(globalState: StoreState, activity?: BonusActivity): boolean {
  const { activityRedemption } = globalState;
  return (
    activityRedemption?.progress === UPLOAD_PROGRESS_UPLOADING &&
    (activity === undefined || (activityRedemption.activities?.includes(activity) ?? false))
  );
}

export type ActivityRedemptionDetail = {
  verificationDate: string;
  activityDetail: string;
};

export type DirectRewardRequest = {
  iban: string;
};

/**
 * Upload bonus activity redemption.
 */
export const uploadBonusActivityRedemption = (
  activities: Array<BonusActivity>,
  documents: Array<File | DocumentData>,
  formDataPartCreator: FormDataPartCreator,
  fetcher?: Fetcher,
  userId?: string,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
) => {
  const body = new FormData();

  body.append(
    'activity',
    JSON.stringify({
      activityIds: activities.map((activity: BonusActivity) => activity.id),
      detail,
      directRewardRequest,
    }),
  );

  documents.forEach((document: File | DocumentData) => {
    body.append('document', formDataPartCreator(document));
  });

  return createAction({
    endpoint: endpointWithUserId('/activities/bonus/redemption', userId),
    types: [
      {
        type: UPLOADING_BONUS_ACTIVITY_REDEMPTION,
        meta: { activities }, // Pass back the activity items on uploading
      },
      SUCCESS_BONUS_ACTIVITY_REDEMPTION,
      FAILED_BONUS_ACTIVITY_REDEMPTION,
    ],
    fetch: fetcher,
    method: HTTP_POST,
    body,
  });
};

/**
 * Upload bonus activity redemption and update all activities afterwards.
 */
export const uploadBonusActivityAndUpdate = (
  activities: Array<BonusActivity>,
  documents: Array<File | DocumentData>,
  formDataPartCreator: FormDataPartCreator,
  fetcher?: Fetcher,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
): ThunkAction => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      dashboard: { activeUserId },
    } = getState();

    return dispatch(
      uploadBonusActivityRedemption(
        activities,
        documents,
        formDataPartCreator,
        fetcher,
        activeUserId,
        detail,
        directRewardRequest,
      ),
    ).then(() => dispatch(loadBonusActivities(true, activeUserId)));
  };
};

export const editBonusActivity = (
  activity: BonusActivity,
  documents: Array<File | DocumentData>,
  newActivityId: number | null | undefined,
  formDataPartCreator: FormDataPartCreator,
  fetcher?: Fetcher,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
): ThunkAction => {
  return (dispatch: Dispatch, getState: GetState) => {
    const {
      dashboard: { activeUserId },
    } = getState();
    const body = new FormData();

    body.append(
      'existing',
      JSON.stringify({
        activityId: activity.id,
        redemptionRequestId: activity.redemptionRequestId,
        attachments:
          activity.attachments == null
            ? []
            : activity.attachments.map((a: Attachment) => a.imagePointer),
        newActivityId,
        detail,
        directRewardRequest,
      }),
    );

    documents.forEach((document: File | DocumentData) => {
      body.append('documents', formDataPartCreator(document));
    });

    return dispatch(
      createAction({
        endpoint: endpointWithUserId('/activities/bonus/edit', activeUserId),
        types: [
          EDIT_BONUS_ACTIVITY_REDEMPTION,
          SUCCESS_BONUS_ACTIVITY_REDEMPTION,
          FAILED_BONUS_ACTIVITY_REDEMPTION,
        ],
        fetch: fetcher,
        method: HTTP_PUT,
        body,
      }),
    );
  };
};

export const uploadBase64Activity = (
  activity: BonusActivity,
  documents: Array<string>,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;

    return dispatch(
      createAction({
        endpoint: endpointWithUserId('/activities/bonus/redemption', activeUserId),
        types: [
          {
            type: UPLOADING_BONUS_ACTIVITY_REDEMPTION,
            meta: { activities: [activity] }, // Pass back the activity items on uploading
          },
          SUCCESS_BONUS_ACTIVITY_REDEMPTION,
          FAILED_BONUS_ACTIVITY_REDEMPTION,
        ],
        method: HTTP_POST,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activityIds: [activity.id],
          files: documents,
          detail,
          directRewardRequest,
        }),
      }),
    );
  };
};

export const uploadBase64ActivityAndUpdate = (
  activity: BonusActivity,
  documents: Array<string>,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;

    return dispatch(uploadBase64Activity(activity, documents, detail, directRewardRequest)).then(
      () => {
        dispatch(loadBonusActivities(true, activeUserId));
        dispatch(loadDashboard(true));
        dispatch(loadBonusTimeline(true, activeUserId));
      },
    );
  };
};

export const editBase64Activity = (
  activity: BonusActivity,
  documents: Array<string>,
  attachments: Array<number>,
  newActivityId: number | null | undefined,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;

    return dispatch(
      createAction({
        endpoint: endpointWithUserId('/activities/bonus/edit', activeUserId),
        types: [
          EDIT_BONUS_ACTIVITY_REDEMPTION,
          SUCCESS_BONUS_ACTIVITY_REDEMPTION,
          FAILED_BONUS_ACTIVITY_REDEMPTION,
        ],
        method: HTTP_PUT,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          activityId: activity.id,
          redemptionRequestId: activity.redemptionRequestId,
          files: documents,
          attachments,
          newActivityId,
          detail,
          directRewardRequest,
        }),
      }),
    );
  };
};

export const editBase64ActivityAndUpdate = (
  activity: BonusActivity,
  documents: Array<string>,
  attachments: Array<number>,
  newActivityId: number | null | undefined,
  detail?: ActivityRedemptionDetail,
  directRewardRequest?: DirectRewardRequest,
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const { activeUserId } = getState().dashboard;

    return dispatch(
      editBase64Activity(
        activity,
        documents,
        attachments,
        newActivityId,
        detail,
        directRewardRequest,
      ),
    ).then(() => {
      dispatch(loadBonusActivities(true, activeUserId));
      dispatch(loadDashboard(true));
      dispatch(loadBonusTimeline(true, activeUserId));
    });
  };
};

/**
 * Tell the reducer we're done with the upload and we can go back to idle.
 * E.g. resets the reducer
 */
export function finishBonusActivityRedemptionUpload(): Action {
  return { type: FINISHED_BONUS_ACTIVITY_REDEMPTION };
}
